import { useMemo, useState } from 'react'
import {
	ArrowDownToLine,
	ArrowUpFromLine,
	Gamepad2,
	Wallet,
	ArrowLeftRight,
	Clock,
	AlertCircle,
	CheckCircle2,
	XCircle,
	Loader2,
	RefreshCw,
	ChevronDown,
	ExternalLink,
	TrendingUp,
	TrendingDown,
	DollarSign,
	Activity,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { th } from 'date-fns/locale'

import { cn } from '@/common/lib/utils'
import { Badge } from '@/common/ui/badge'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/common/ui/collapsible'
import { Skeleton } from '@/common/ui/skeleton'
import { Button } from '@/common/ui/button'

import { useQueryPlayerChatStats } from '../../../api/hooks'

// Route helpers
const ROUTES = {
	depositDetail: (txId: string) => `/operator/transactions/deposits/${txId}`,
	withdrawDetail: (txId: string) => `/operator/transactions/withdraws/${txId}`,
	playerDetail: (id: string) => `/operator/get-player-list/player-detail/${id}`,
}

interface PlayerStatsPanelProps {
	roomId: string
	hasPlayer: boolean
}

// Status badge component
function StatusBadge({
	status,
	needsApproval,
}: {
	status: string
	needsApproval?: boolean
}) {
	const statusConfig: Record<
		string,
		{
			label: string
			variant: 'default' | 'secondary' | 'destructive' | 'outline'
			icon: React.ReactNode
		}
	> = {
		COMPLETED: {
			label: 'สำเร็จ',
			variant: 'default',
			icon: <CheckCircle2 className="h-3 w-3" />,
		},
		MANUAL_COMPLETED: {
			label: 'สำเร็จ (Manual)',
			variant: 'default',
			icon: <CheckCircle2 className="h-3 w-3" />,
		},
		PENDING: {
			label: needsApproval ? 'รออนุมัติ' : 'รอดำเนินการ',
			variant: 'secondary',
			icon: needsApproval ? (
				<AlertCircle className="h-3 w-3" />
			) : (
				<Clock className="h-3 w-3" />
			),
		},
		PROCESSING: {
			label: 'กำลังดำเนินการ',
			variant: 'secondary',
			icon: <Loader2 className="h-3 w-3 animate-spin" />,
		},
		REJECTED: {
			label: 'ปฏิเสธ',
			variant: 'destructive',
			icon: <XCircle className="h-3 w-3" />,
		},
		CANCELLED: {
			label: 'ยกเลิก',
			variant: 'outline',
			icon: <XCircle className="h-3 w-3" />,
		},
		FAILED: {
			label: 'ล้มเหลว',
			variant: 'destructive',
			icon: <XCircle className="h-3 w-3" />,
		},
		EXPIRED: {
			label: 'หมดอายุ',
			variant: 'outline',
			icon: <Clock className="h-3 w-3" />,
		},
	}

	const config = statusConfig[status] || {
		label: status,
		variant: 'outline' as const,
		icon: null,
	}

	return (
		<Badge
			variant={config.variant}
			className={cn(
				'gap-1 text-[10px]',
				needsApproval && status === 'PENDING' && 'bg-amber-500 text-white',
			)}
		>
			{config.icon}
			{config.label}
		</Badge>
	)
}

// Format currency
function formatCurrency(amount: number): string {
	return new Intl.NumberFormat('th-TH', {
		style: 'currency',
		currency: 'THB',
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(amount)
}

// Stat item component
function StatItem({
	icon,
	label,
	value,
	subValue,
	badge,
	highlight,
	onClick,
	href,
}: {
	icon: React.ReactNode
	label: string
	value: React.ReactNode
	subValue?: React.ReactNode
	badge?: React.ReactNode
	highlight?: boolean
	onClick?: () => void
	href?: string
}) {
	const isClickable = !!onClick || !!href

	const handleClick = () => {
		if (href) {
			window.open(href, '_blank')
		} else if (onClick) {
			onClick()
		}
	}

	return (
		<div
			className={cn(
				'flex items-start gap-3 rounded-md px-3 py-2.5',
				highlight && 'bg-amber-50 dark:bg-amber-950/30',
				isClickable &&
					'cursor-pointer transition-colors hover:bg-accent/50 active:bg-accent',
			)}
			onClick={isClickable ? handleClick : undefined}
			role={isClickable ? 'button' : undefined}
			tabIndex={isClickable ? 0 : undefined}
			onKeyDown={
				isClickable
					? (e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault()
								handleClick()
							}
						}
					: undefined
			}
		>
			<div className="mt-0.5 text-muted-foreground">{icon}</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-center justify-between gap-2">
					<span className="text-sm text-muted-foreground">{label}</span>
					<div className="flex items-center gap-1">
						{badge}
						{isClickable && (
							<ExternalLink className="h-3 w-3 text-muted-foreground" />
						)}
					</div>
				</div>
				<div className="text-base font-semibold">{value}</div>
				{subValue && (
					<div className="text-xs text-muted-foreground">{subValue}</div>
				)}
			</div>
		</div>
	)
}

// Loading skeleton
function PlayerStatsSkeleton() {
	return (
		<div className="space-y-2 p-2">
			{Array.from({ length: 5 }).map((_, i) => (
				<div key={i} className="flex items-start gap-2 p-2">
					<Skeleton className="h-4 w-4 rounded" />
					<div className="flex-1 space-y-1">
						<Skeleton className="h-3 w-16" />
						<Skeleton className="h-4 w-24" />
					</div>
				</div>
			))}
		</div>
	)
}

// Empty state
function EmptyState() {
	return (
		<div className="flex flex-col items-center justify-center py-6 text-center text-muted-foreground">
			<Wallet className="mb-2 h-8 w-8 opacity-50" />
			<p className="text-sm">ยังไม่ได้เชื่อมต่อผู้เล่น</p>
			<p className="text-xs">กรุณาเชื่อมต่อผู้เล่นเพื่อดูข้อมูล</p>
		</div>
	)
}

export function PlayerStatsPanel({ roomId, hasPlayer }: PlayerStatsPanelProps) {
	const [isOpen, setIsOpen] = useState(true)
	const {
		data: stats,
		isLoading,
		refetch,
		isFetching,
	} = useQueryPlayerChatStats(roomId, hasPlayer)

	// Memoize wallet balances display
	const walletDisplay = useMemo(() => {
		if (!stats?.walletBalances?.length) return null

		const mainWallet = stats.walletBalances.find((w) => w.type === 'MAIN')
		const promotionWallet = stats.walletBalances.find(
			(w) => w.type === 'PROMOTION',
		)

		return { mainWallet, promotionWallet }
	}, [stats?.walletBalances])

	if (!hasPlayer) {
		return <EmptyState />
	}

	if (isLoading) {
		return <PlayerStatsSkeleton />
	}

	if (!stats || !stats.playerId) {
		return <EmptyState />
	}

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen}>
			<CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-3 py-2 text-base font-semibold hover:bg-accent">
				<div className="flex items-center gap-2">
					<span>ข้อมูลลูกค้า</span>
					{stats.isNewUser && (
						<Badge variant="default" className="bg-green-500 text-xs">
							ลูกค้าใหม่
						</Badge>
					)}
				</div>
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						className="h-7 w-7"
						onClick={(e) => {
							e.stopPropagation()
							refetch()
						}}
						disabled={isFetching}
					>
						<RefreshCw
							className={cn('h-4 w-4', isFetching && 'animate-spin')}
						/>
					</Button>
					<ChevronDown
						className={cn(
							'h-4 w-4 transition-transform duration-200',
							isOpen && 'rotate-180',
						)}
					/>
				</div>
			</CollapsibleTrigger>
			<CollapsibleContent>
				<div className="space-y-1 py-2">
					{/* Last Online */}
					<StatItem
						icon={<Activity className="h-5 w-5 text-emerald-500" />}
						label="ออนไลน์ล่าสุด"
						value={
							stats.lastOnline ? (
								formatDistanceToNow(new Date(stats.lastOnline), {
									addSuffix: true,
									locale: th,
								})
							) : (
								<span className="text-muted-foreground">-</span>
							)
						}
						subValue={
							stats.lastOnline &&
							format(new Date(stats.lastOnline), 'dd/MM/yyyy HH:mm', {
								locale: th,
							})
						}
					/>

					{/* Last Deposit */}
					<StatItem
						icon={<ArrowDownToLine className="h-5 w-5 text-green-500" />}
						label="ฝากล่าสุด"
						value={
							stats.lastDeposit ? (
								formatCurrency(stats.lastDeposit.amount)
							) : (
								<span className="text-muted-foreground">-</span>
							)
						}
						subValue={
							stats.lastDeposit &&
							formatDistanceToNow(new Date(stats.lastDeposit.createdAt), {
								addSuffix: true,
								locale: th,
							})
						}
						badge={
							stats.lastDeposit && (
								<StatusBadge status={stats.lastDeposit.status} />
							)
						}
						href={
							stats.lastDeposit
								? ROUTES.depositDetail(stats.lastDeposit.txId)
								: undefined
						}
					/>

					{/* Last Withdraw */}
					<StatItem
						icon={<ArrowUpFromLine className="h-5 w-5 text-red-500" />}
						label="ถอนล่าสุด"
						value={
							stats.lastWithdraw ? (
								formatCurrency(stats.lastWithdraw.amount)
							) : (
								<span className="text-muted-foreground">-</span>
							)
						}
						subValue={
							stats.lastWithdraw &&
							formatDistanceToNow(new Date(stats.lastWithdraw.createdAt), {
								addSuffix: true,
								locale: th,
							})
						}
						badge={
							stats.lastWithdraw && (
								<StatusBadge
									status={stats.lastWithdraw.status}
									needsApproval={stats.lastWithdraw.needsApproval}
								/>
							)
						}
						highlight={stats.lastWithdraw?.needsApproval}
						href={
							stats.lastWithdraw
								? ROUTES.withdrawDetail(stats.lastWithdraw.txId)
								: undefined
						}
					/>

					{/* Game Play Stats */}
					<StatItem
						icon={<Gamepad2 className="h-5 w-5 text-purple-500" />}
						label="เล่นวันนี้"
						value={
							<span>
								{stats.gamePlayStats.playCountToday} ครั้ง •{' '}
								{formatCurrency(stats.gamePlayStats.turnoverToday)}
							</span>
						}
						subValue={
							stats.gamePlayStats.lastPlayAt ? (
								<span>
									เล่นล่าสุด:{' '}
									{format(new Date(stats.gamePlayStats.lastPlayAt), 'HH:mm', {
										locale: th,
									})}
									{stats.gamePlayStats.lastGameName && (
										<>
											{' • '}
											{stats.gamePlayStats.lastGameName}
											{stats.gamePlayStats.lastProviderName && (
												<span className="text-muted-foreground/70">
													{' '}
													({stats.gamePlayStats.lastProviderName})
												</span>
											)}
										</>
									)}
								</span>
							) : (
								'ยังไม่มีการเล่นวันนี้'
							)
						}
						href={
							stats.playerId ? ROUTES.playerDetail(stats.playerId) : undefined
						}
					/>

					{/* Play Log Summary Stats */}
					{stats.playLogSummary && (
						<StatItem
							icon={<DollarSign className="h-5 w-5 text-cyan-500" />}
							label="การเล่นล่าสุด 30 นาที"
							value={
								<div className="flex flex-col gap-0.5">
									<span className="flex items-center gap-1">
										<TrendingUp className="h-3.5 w-3.5 text-green-500" />
										<span className="text-xs text-muted-foreground">
											แทงได้:
										</span>{' '}
										<span className="text-green-600">
											{formatCurrency(stats.playLogSummary.transferOut)}
										</span>
									</span>
									<span className="flex items-center gap-1">
										<TrendingDown className="h-3.5 w-3.5 text-red-500" />
										<span className="text-xs text-muted-foreground">
											แทงเสีย:
										</span>{' '}
										<span className="text-red-600">
											{formatCurrency(stats.playLogSummary.transferIn)}
										</span>
									</span>
									<span className="flex items-center gap-1">
										<DollarSign className="h-3.5 w-3.5 text-blue-500" />
										<span className="text-xs text-muted-foreground">
											กำไร:
										</span>{' '}
										<span
											className={cn(
												'font-semibold',
												stats.playLogSummary.profit >= 0
													? 'text-green-600'
													: 'text-red-600',
											)}
										>
											{formatCurrency(stats.playLogSummary.profit)}
										</span>
									</span>
								</div>
							}
						/>
					)}

					{/* Wallet Balances */}
					{walletDisplay && (
						<StatItem
							icon={<Wallet className="h-5 w-5 text-blue-500" />}
							label="ยอดเงินคงเหลือ"
							value={
								<div className="flex flex-col gap-0.5">
									<span>
										หลัก:{' '}
										{formatCurrency(walletDisplay.mainWallet?.balance || 0)}
									</span>
									{walletDisplay.promotionWallet && (
										<span className="text-xs text-muted-foreground">
											โปรโมชั่น:{' '}
											{formatCurrency(walletDisplay.promotionWallet.balance)}
											{walletDisplay.promotionWallet.promotionName && (
												<span className="ml-1">
													({walletDisplay.promotionWallet.promotionName})
												</span>
											)}
										</span>
									)}
								</div>
							}
						/>
					)}

					{/* Last Wallet Transfer */}
					<StatItem
						icon={<ArrowLeftRight className="h-5 w-5 text-orange-500" />}
						label="โอนกระเป๋าล่าสุด"
						value={
							stats.lastWalletTransfer ? (
								<span>
									{formatCurrency(stats.lastWalletTransfer.amount)}{' '}
									<span className="text-xs text-muted-foreground">
										({stats.lastWalletTransfer.fromWalletType} →{' '}
										{stats.lastWalletTransfer.toWalletType})
									</span>
								</span>
							) : (
								<span className="text-muted-foreground">-</span>
							)
						}
						subValue={
							stats.lastWalletTransfer &&
							formatDistanceToNow(
								new Date(stats.lastWalletTransfer.createdAt),
								{
									addSuffix: true,
									locale: th,
								},
							)
						}
					/>
				</div>
			</CollapsibleContent>
		</Collapsible>
	)
}
